import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Router as RouterIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoFixHigh as AutoFixHighIcon,
  ContentCopy as CopyIcon,
  Assignment as AuditIcon,
  FilterList as FilterIcon,
  PlayArrow as PlayArrowIcon,
  SkipNext as SkipNextIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { baseIPApi } from '../../services/apiClient';

const BaseIPManagement = () => {
  const { user } = useAuth();
  const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();
  const isAdmin = user?.role === 'admin';

  // Sites state
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteSearchTerm, setSiteSearchTerm] = useState('');

  // Units state
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitSearchTerm, setUnitSearchTerm] = useState('');
  const [unitFilter, setUnitFilter] = useState('all');

  // Edit state
  const [editingUnit, setEditingUnit] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Bulk action state
  const [selectedUnits, setSelectedUnits] = useState(new Set());
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });

  // Audit state
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  // Site data from API
  const [siteData, setSiteData] = useState(null);

  // Quick assign mode
  const [quickAssignMode, setQuickAssignMode] = useState(null); // 'unit-first' or 'ip-first'
  const [quickAssignIP, setQuickAssignIP] = useState('');
  const [quickAssignUnit, setQuickAssignUnit] = useState(null);

  // Duplicate confirmation dialog
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    unitId: null,
    unitName: '',
    baseIP: '',
    duplicates: [],
    onConfirm: null
  });

  // ============================================================
  // Data Loading
  // ============================================================

  const loadSites = useCallback(async () => {
    try {
      setSitesLoading(true);
      const response = await baseIPApi.getSites();
      if (response.data.success) {
        setSites(response.data.data.sites);
      }
    } catch (error) {
      console.error('Error loading sites:', error);
      enqueueSnackbar('Failed to load sites', { variant: 'error' });
    } finally {
      setSitesLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadUnitsForSite = useCallback(async (siteId) => {
    try {
      setUnitsLoading(true);
      setUnits([]);
      const response = await baseIPApi.getUnitsForSite(siteId);
      if (response.data.success) {
        setUnits(response.data.data.units);
        setSiteData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading units:', error);
      enqueueSnackbar('Failed to load units', { variant: 'error' });
    } finally {
      setUnitsLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadAuditReport = useCallback(async () => {
    try {
      setAuditLoading(true);
      const response = await baseIPApi.getAuditReport();
      if (response.data.success) {
        setAuditData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading audit:', error);
      enqueueSnackbar('Failed to load audit report', { variant: 'error' });
    } finally {
      setAuditLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  useEffect(() => {
    if (selectedSite) {
      loadUnitsForSite(selectedSite.siteId);
      setSelectedUnits(new Set());
    }
  }, [selectedSite, loadUnitsForSite]);

  // Keyboard shortcut: Enter to confirm quick assign
  const quickAssignRef = React.useRef({ unit: null, ip: '', saving: false });
  quickAssignRef.current = { unit: quickAssignUnit, ip: quickAssignIP, saving };

  useEffect(() => {
    const handleKeyDown = async (e) => {
      if (e.key === 'Enter' && quickAssignMode) {
        const { unit, ip, saving: isSaving } = quickAssignRef.current;
        if (unit && ip && !isSaving) {
          e.preventDefault();
          // Trigger the button click programmatically
          document.getElementById('quick-assign-confirm-btn')?.click();
        }
      }
    };

    if (quickAssignMode) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [quickAssignMode]);

  // ============================================================
  // Filtering
  // ============================================================

  const filteredSites = useMemo(() => {
    if (!siteSearchTerm) return sites;
    const term = siteSearchTerm.toLowerCase();
    return sites.filter(s =>
      s.siteName?.toLowerCase().includes(term)
    );
  }, [sites, siteSearchTerm]);

  const filteredUnits = useMemo(() => {
    let result = units;

    // Filter by status
    if (unitFilter === 'missing') {
      result = result.filter(u => u.status === 'missing');
    } else if (unitFilter === 'configured') {
      result = result.filter(u => u.status === 'configured');
    } else if (unitFilter === 'duplicate') {
      result = result.filter(u => u.status === 'duplicate');
    }

    // Filter by search term
    if (unitSearchTerm) {
      const term = unitSearchTerm.toLowerCase();
      result = result.filter(u =>
        u.unitName?.toLowerCase().includes(term) ||
        u.currentIP?.includes(term) ||
        u.suggestedIP?.includes(term)
      );
    }

    return result;
  }, [units, unitFilter, unitSearchTerm]);

  // ============================================================
  // Actions
  // ============================================================

  const handleSiteSelect = (site) => {
    setSelectedSite(site);
    setUnitFilter('all');
    setUnitSearchTerm('');
    // Reset quick assign mode when changing sites
    setQuickAssignMode(null);
    setQuickAssignIP('');
    setQuickAssignUnit(null);
  };

  const handleEditStart = (unit) => {
    setEditingUnit(unit.unitId);
    setEditValue(unit.currentIP || unit.suggestedIP || '');
  };

  const handleEditCancel = () => {
    setEditingUnit(null);
    setEditValue('');
  };

  const handleSaveIP = async (unitId, force = false) => {
    if (!editValue.trim()) {
      enqueueSnackbar('Please enter an IP address', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      const response = await baseIPApi.updateBaseIP(unitId, editValue.trim(), force);

      if (response.data.success) {
        enqueueSnackbar('BaseIP saved successfully', { variant: 'success' });
        setEditingUnit(null);
        setEditValue('');
        // Reload units to get updated data
        loadUnitsForSite(selectedSite.siteId);
        // Reload sites to update stats
        loadSites();
      }
    } catch (error) {
      // Check if this is a duplicate warning that requires confirmation
      if (error.response?.status === 409 && error.response?.data?.requiresConfirmation) {
        const { duplicates, message } = error.response.data;
        const unit = units.find(u => u.unitId === unitId);
        setDuplicateDialog({
          open: true,
          unitId,
          unitName: unit?.unitName || '',
          baseIP: editValue.trim(),
          duplicates,
          onConfirm: () => handleSaveIP(unitId, true) // Retry with force=true
        });
      } else {
        const errorMsg = error.response?.data?.error || 'Failed to save baseIP';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleApplySuggestion = async (unit, force = false) => {
    if (!unit.suggestedIP) return;

    try {
      setSaving(true);
      const response = await baseIPApi.updateBaseIP(unit.unitId, unit.suggestedIP, force);

      if (response.data.success) {
        enqueueSnackbar(`Applied ${unit.suggestedIP} to ${unit.unitName}`, { variant: 'success' });
        loadUnitsForSite(selectedSite.siteId);
        loadSites();
      }
    } catch (error) {
      // Check if this is a duplicate warning that requires confirmation
      if (error.response?.status === 409 && error.response?.data?.requiresConfirmation) {
        const { duplicates } = error.response.data;
        setDuplicateDialog({
          open: true,
          unitId: unit.unitId,
          unitName: unit.unitName,
          baseIP: unit.suggestedIP,
          duplicates,
          onConfirm: () => handleApplySuggestion(unit, true)
        });
      } else {
        const errorMsg = error.response?.data?.error || 'Failed to apply suggestion';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBulkApplySuggestions = async () => {
    const unitsToAssign = filteredUnits
      .filter(u => u.status === 'missing' && u.suggestedIP && selectedUnits.has(u.unitId));

    if (unitsToAssign.length === 0) {
      enqueueSnackbar('No units with suggestions selected', { variant: 'warning' });
      return;
    }

    const assignments = unitsToAssign.map(u => ({
      locationId: u.unitId,
      baseIP: u.suggestedIP
    }));

    try {
      setBulkAssigning(true);
      setBulkProgress({ current: 0, total: assignments.length });

      const response = await baseIPApi.bulkAssign(selectedSite.siteId, assignments);

      if (response.data.success) {
        const { successful, failed } = response.data.data.results;
        setBulkProgress({ current: assignments.length, total: assignments.length });

        if (failed.length === 0) {
          enqueueSnackbar(`Successfully assigned ${successful.length} IPs`, { variant: 'success' });
        } else {
          enqueueSnackbar(
            `Assigned ${successful.length} IPs, ${failed.length} failed`,
            { variant: 'warning' }
          );
        }

        setSelectedUnits(new Set());
        loadUnitsForSite(selectedSite.siteId);
        loadSites();
      }
    } catch (error) {
      enqueueSnackbar('Bulk assignment failed', { variant: 'error' });
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleSelectAll = () => {
    const allIds = filteredUnits
      .filter(u => u.status === 'missing' && u.suggestedIP)
      .map(u => u.unitId);
    setSelectedUnits(new Set(allIds));
  };

  const handleDeselectAll = () => {
    setSelectedUnits(new Set());
  };

  const handleToggleUnit = (unitId) => {
    setSelectedUnits(prev => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
  };

  const handleOpenAudit = () => {
    setAuditOpen(true);
    loadAuditReport();
  };

  // Quick Assign Mode handlers
  const handleStartQuickAssign = (mode) => {
    setQuickAssignMode(mode);
    setQuickAssignIP('');
    setQuickAssignUnit(null);
  };

  const handleExitQuickAssign = () => {
    setQuickAssignMode(null);
    setQuickAssignIP('');
    setQuickAssignUnit(null);
  };

  const handleQuickAssignUnitClick = (unit) => {
    if (!quickAssignMode) return;

    if (quickAssignMode === 'unit-first') {
      // Select unit, prepare IP input
      setQuickAssignUnit(unit);
      // Pre-fill with suggested IP or next available
      if (!quickAssignIP) {
        setQuickAssignIP(unit.suggestedIP || siteData?.nextAvailableIP || '');
      }
    } else if (quickAssignMode === 'ip-first') {
      // IP is already set, assign to this unit
      setQuickAssignUnit(unit);
    }
  };

  const handleQuickAssignConfirm = async (force = false) => {
    if (!quickAssignUnit || !quickAssignIP) return;

    try {
      setSaving(true);
      const response = await baseIPApi.updateBaseIP(quickAssignUnit.unitId, quickAssignIP.trim(), force);

      if (response.data.success) {
        enqueueSnackbar(`Assigned ${quickAssignIP} to ${quickAssignUnit.unitName}`, { variant: 'success' });

        // Increment IP for next assignment
        const parts = quickAssignIP.split('.');
        if (parts.length === 4) {
          const lastOctet = parseInt(parts[3], 10);
          if (lastOctet < 254) {
            setQuickAssignIP(`${parts[0]}.${parts[1]}.${parts[2]}.${lastOctet + 1}`);
          }
        }

        setQuickAssignUnit(null);
        loadUnitsForSite(selectedSite.siteId);
        loadSites();
      }
    } catch (error) {
      // Check if this is a duplicate warning that requires confirmation
      if (error.response?.status === 409 && error.response?.data?.requiresConfirmation) {
        const { duplicates } = error.response.data;
        setDuplicateDialog({
          open: true,
          unitId: quickAssignUnit.unitId,
          unitName: quickAssignUnit.unitName,
          baseIP: quickAssignIP.trim(),
          duplicates,
          onConfirm: () => handleQuickAssignConfirm(true)
        });
      } else {
        const errorMsg = error.response?.data?.error || 'Failed to assign IP';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleQuickAssignSkip = () => {
    // Skip current unit, increment IP anyway
    const parts = quickAssignIP.split('.');
    if (parts.length === 4) {
      const lastOctet = parseInt(parts[3], 10);
      if (lastOctet < 254) {
        setQuickAssignIP(`${parts[0]}.${parts[1]}.${parts[2]}.${lastOctet + 1}`);
      }
    }
    setQuickAssignUnit(null);
  };

  // ============================================================
  // Render Helpers
  // ============================================================

  const getStatusChip = (status) => {
    switch (status) {
      case 'configured':
        return <Chip size="small" label="Configured" color="success" icon={<CheckCircleIcon />} />;
      case 'missing':
        return <Chip size="small" label="Missing" color="warning" icon={<WarningIcon />} />;
      case 'duplicate':
        return <Chip size="small" label="Duplicate" color="error" icon={<ErrorIcon />} />;
      default:
        return null;
    }
  };

  const getSiteStatusColor = (status) => {
    switch (status) {
      case 'complete': return 'success.main';
      case 'partial': return 'warning.main';
      case 'missing': return 'error.main';
      default: return 'text.secondary';
    }
  };

  // ============================================================
  // Access Check
  // ============================================================

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t ? t('BaseIPManagement.admin_required') : 'Admin access required.'}
        </Alert>
      </Box>
    );
  }

  // ============================================================
  // Main Render
  // ============================================================

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left Panel - Site List */}
      <Box
        sx={{
          width: 350,
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper'
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RouterIcon color="primary" />
              Base IP Management
            </Typography>
            <Box>
              <Tooltip title="Audit Report">
                <IconButton onClick={handleOpenAudit} size="small">
                  <AuditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={loadSites} size="small" disabled={sitesLoading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <TextField
            size="small"
            fullWidth
            placeholder="Search sites..."
            value={siteSearchTerm}
            onChange={(e) => setSiteSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Site List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {sitesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredSites.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No sites found</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredSites.map((site) => (
                <ListItem key={site.siteId} disablePadding divider>
                  <ListItemButton
                    selected={selectedSite?.siteId === site.siteId}
                    onClick={() => handleSiteSelect(site)}
                  >
                    <ListItemIcon>
                      <Badge
                        badgeContent={site.missingUnits}
                        color={site.status === 'complete' ? 'success' : 'warning'}
                        max={99}
                      >
                        <BusinessIcon sx={{ color: getSiteStatusColor(site.status) }} />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={site.siteName}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {site.ipPrefix || 'No prefix'}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${site.configuredUnits}/${site.totalUnits}`}
                            color={site.status === 'complete' ? 'success' : 'default'}
                            sx={{ height: 18, fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* Right Panel - Unit Details */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedSite ? (
          <>
            {/* Unit Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h6">{selectedSite.siteName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    IP Prefix: {siteData?.site?.ipPrefix || 'Not configured'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!quickAssignMode ? (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleStartQuickAssign('unit-first')}
                      >
                        Quick Assign (Unit→IP)
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleStartQuickAssign('ip-first')}
                      >
                        Quick Assign (IP→Unit)
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<StopIcon />}
                      onClick={handleExitQuickAssign}
                    >
                      Exit Quick Assign
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSelectAll}
                    disabled={bulkAssigning || quickAssignMode}
                  >
                    Select All Missing
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={bulkAssigning ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                    onClick={handleBulkApplySuggestions}
                    disabled={selectedUnits.size === 0 || bulkAssigning || quickAssignMode}
                  >
                    Apply Selected ({selectedUnits.size})
                  </Button>
                  <IconButton
                    onClick={() => loadUnitsForSite(selectedSite.siteId)}
                    disabled={unitsLoading}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Quick Assign Mode Bar */}
              {quickAssignMode && (
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    mt: 2,
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap'
                  }}
                >
                  <Typography variant="subtitle2">
                    {quickAssignMode === 'unit-first'
                      ? 'Click a unit to assign an IP'
                      : 'Enter IP, then click a unit to assign'}
                  </Typography>

                  <TextField
                    size="small"
                    label="IP Address"
                    value={quickAssignIP}
                    onChange={(e) => setQuickAssignIP(e.target.value)}
                    placeholder={siteData?.site?.ipPrefix ? `${siteData.site.ipPrefix}X` : '10.X.0.Y'}
                    sx={{
                      width: 180,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      '& .MuiInputBase-root': { bgcolor: 'background.paper' }
                    }}
                  />

                  {quickAssignUnit && (
                    <Chip
                      label={`Selected: ${quickAssignUnit.unitName}`}
                      color="secondary"
                      onDelete={() => setQuickAssignUnit(null)}
                    />
                  )}

                  <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                    <Button
                      id="quick-assign-confirm-btn"
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={() => handleQuickAssignConfirm(false)}
                      disabled={!quickAssignUnit || !quickAssignIP || saving}
                    >
                      Assign & Next
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SkipNextIcon />}
                      onClick={handleQuickAssignSkip}
                      disabled={!quickAssignIP}
                      sx={{ bgcolor: 'background.paper' }}
                    >
                      Skip IP
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Filters */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search units..."
                  value={unitSearchTerm}
                  onChange={(e) => setUnitSearchTerm(e.target.value)}
                  sx={{ width: 250 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
                <ToggleButtonGroup
                  size="small"
                  value={unitFilter}
                  exclusive
                  onChange={(e, val) => val && setUnitFilter(val)}
                >
                  <ToggleButton value="all">
                    All ({units.length})
                  </ToggleButton>
                  <ToggleButton value="missing">
                    <Badge badgeContent={siteData?.summary?.missing || 0} color="warning" max={99}>
                      Missing
                    </Badge>
                  </ToggleButton>
                  <ToggleButton value="configured">
                    Configured
                  </ToggleButton>
                  <ToggleButton value="duplicate">
                    <Badge badgeContent={siteData?.summary?.duplicates || 0} color="error" max={99}>
                      Duplicates
                    </Badge>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Duplicate warnings */}
              {siteData?.duplicateIPs?.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>Duplicate IPs Detected</AlertTitle>
                  {siteData.duplicateIPs.map((dup, idx) => (
                    <Typography key={idx} variant="body2">
                      {dup.ip}: {dup.unitNames.join(', ')}
                    </Typography>
                  ))}
                </Alert>
              )}
            </Box>

            {/* Units Table */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {unitsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : filteredUnits.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography color="text.secondary">No units found</Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedUnits.size > 0 && selectedUnits.size < filteredUnits.filter(u => u.status === 'missing').length}
                            checked={selectedUnits.size > 0 && selectedUnits.size === filteredUnits.filter(u => u.status === 'missing' && u.suggestedIP).length}
                            onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                          />
                        </TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell>Building / Floor</TableCell>
                        <TableCell>Current IP</TableCell>
                        <TableCell>Suggested IP</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUnits.map((unit) => (
                        <TableRow
                          key={unit.unitId}
                          onClick={() => quickAssignMode && unit.status === 'missing' && handleQuickAssignUnitClick(unit)}
                          sx={{
                            bgcolor: unit.status === 'duplicate' ? 'error.lighter' :
                                     unit.status === 'missing' ? 'warning.lighter' : 'inherit',
                            '&:hover': { bgcolor: 'action.hover' },
                            cursor: quickAssignMode && unit.status === 'missing' ? 'pointer' : 'default',
                            outline: quickAssignUnit?.unitId === unit.unitId ? '2px solid' : 'none',
                            outlineColor: 'primary.main'
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedUnits.has(unit.unitId)}
                              onChange={() => handleToggleUnit(unit.unitId)}
                              disabled={unit.status !== 'missing' || !unit.suggestedIP}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {unit.unitName}
                              </Typography>
                              {unit.locationType && unit.locationType !== 4 && (
                                <Chip
                                  size="small"
                                  label={unit.locationTypeName || 'Other'}
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {unit.buildingName} / {unit.floorName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {editingUnit === unit.unitId ? (
                              <TextField
                                size="small"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                placeholder="10.X.0.Y"
                                sx={{ width: 130 }}
                                autoFocus
                                disabled={saving}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  color: unit.currentIP ? 'text.primary' : 'text.disabled'
                                }}
                              >
                                {unit.currentIP || '—'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {unit.suggestedIP && !unit.currentIP && (
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                              >
                                {unit.suggestedIP}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {getStatusChip(unit.status)}
                          </TableCell>
                          <TableCell align="right">
                            {editingUnit === unit.unitId ? (
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSaveIP(unit.unitId)}
                                  disabled={saving}
                                >
                                  {saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleEditCancel}
                                  disabled={saving}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                {unit.suggestedIP && !unit.currentIP && (
                                  <Tooltip title="Apply suggestion">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleApplySuggestion(unit)}
                                      disabled={saving}
                                    >
                                      <AutoFixHighIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Edit IP">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditStart(unit)}
                                    disabled={saving}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <RouterIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Select a site to manage baseIP addresses
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                Choose a site from the left panel to view and configure unit IPs
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Audit Dialog */}
      <Dialog open={auditOpen} onClose={() => setAuditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AuditIcon color="primary" />
            BaseIP Audit Report
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {auditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : auditData ? (
            <Box>
              {/* Summary */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Card variant="outlined" sx={{ flex: 1, minWidth: 120 }}>
                  <CardContent sx={{ textAlign: 'center', py: 1 }}>
                    <Typography variant="h4">{auditData.summary.totalUnits}</Typography>
                    <Typography variant="caption" color="text.secondary">Total Units</Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ flex: 1, minWidth: 120 }}>
                  <CardContent sx={{ textAlign: 'center', py: 1 }}>
                    <Typography variant="h4" color="success.main">
                      {auditData.summary.configuredUnits}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Configured</Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ flex: 1, minWidth: 120 }}>
                  <CardContent sx={{ textAlign: 'center', py: 1 }}>
                    <Typography variant="h4" color="warning.main">
                      {auditData.summary.missingCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Missing</Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ flex: 1, minWidth: 120 }}>
                  <CardContent sx={{ textAlign: 'center', py: 1 }}>
                    <Typography variant="h4" color="error.main">
                      {auditData.summary.duplicateCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Duplicates</Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Issues */}
              {auditData.summary.totalIssues === 0 ? (
                <Alert severity="success">
                  <AlertTitle>All Clear!</AlertTitle>
                  No issues found. All units have valid baseIP configurations.
                </Alert>
              ) : (
                <>
                  {auditData.issues.duplicates.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <AlertTitle>Duplicate IPs ({auditData.issues.duplicates.length})</AlertTitle>
                      {auditData.issues.duplicates.map((dup, idx) => (
                        <Typography key={idx} variant="body2">
                          <strong>{dup.siteName}</strong> - {dup.ip}: {dup.units.map(u => u.unitName).join(', ')}
                        </Typography>
                      ))}
                    </Alert>
                  )}

                  {auditData.issues.wrongSiteNumber.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <AlertTitle>Wrong Site Number ({auditData.issues.wrongSiteNumber.length})</AlertTitle>
                      {auditData.issues.wrongSiteNumber.slice(0, 10).map((issue, idx) => (
                        <Typography key={idx} variant="body2">
                          <strong>{issue.unitName}</strong> ({issue.siteName}): {issue.currentIP} - {issue.error}
                        </Typography>
                      ))}
                      {auditData.issues.wrongSiteNumber.length > 10 && (
                        <Typography variant="body2" color="text.secondary">
                          ...and {auditData.issues.wrongSiteNumber.length - 10} more
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {auditData.issues.invalidFormat.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <AlertTitle>Invalid Format ({auditData.issues.invalidFormat.length})</AlertTitle>
                      {auditData.issues.invalidFormat.slice(0, 10).map((issue, idx) => (
                        <Typography key={idx} variant="body2">
                          <strong>{issue.unitName}</strong>: {issue.currentIP} - {issue.error}
                        </Typography>
                      ))}
                    </Alert>
                  )}

                  {auditData.issues.missing.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <AlertTitle>Missing BaseIP ({auditData.issues.missing.length})</AlertTitle>
                      <Typography variant="body2">
                        {auditData.issues.missing.length} units are missing baseIP configuration.
                        Select a site to configure them.
                      </Typography>
                    </Alert>
                  )}
                </>
              )}
            </Box>
          ) : (
            <Typography>No data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditOpen(false)}>Close</Button>
          <Button onClick={loadAuditReport} startIcon={<RefreshIcon />}>
            Refresh
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Confirmation Dialog */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ ...duplicateDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Duplicate IP Detected
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>This IP is already in use</AlertTitle>
            The IP address <strong>{duplicateDialog.baseIP}</strong> is already assigned to the following unit(s):
          </Alert>
          <Box sx={{ pl: 2 }}>
            {duplicateDialog.duplicates.map((dup, idx) => (
              <Typography key={idx} variant="body2" sx={{ py: 0.5 }}>
                • <strong>{dup.name}</strong> (ID: {dup.id})
              </Typography>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Assigning this IP to <strong>{duplicateDialog.unitName}</strong> will create a duplicate.
            This may be intentional (e.g., different VLANs), but please confirm.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDuplicateDialog({ ...duplicateDialog, open: false })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setDuplicateDialog({ ...duplicateDialog, open: false });
              if (duplicateDialog.onConfirm) {
                duplicateDialog.onConfirm();
              }
            }}
          >
            Assign Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BaseIPManagement;
